import type { Circuit } from '@multirx/shared/types/track.js';
import type { GridStyle } from '../game/track/trackCircuitRender.js';
import { readGameCanvasColor } from '../ui/theme.js';
import {
  CanvasTrackDrawSurface,
  calculateTrackOffset,
  calculateTrackZoomFit,
  GAME_TRACK_CELL_SIZE,
  renderCircuit,
  TRACK_FIT_PADDING,
} from '../game/track/trackCircuitRender.js';

export { GAME_TRACK_CELL_SIZE } from '../game/track/trackCircuitRender.js';

/**
 * Dessine un circuit dans un canvas 2D — même rendu et cadrage que le plateau en course
 * (TrackRenderer + fitCameraToCircuit avec cellSize 128 et FIT_PADDING 0.9).
 */
export function drawCircuitInCanvas(
  canvas: HTMLCanvasElement,
  circuit: Circuit,
  options?: {
    gridStyle?: GridStyle;
    backgroundColor?: string;
    /** Largeur du viewport (px), comme l'écran de jeu. */
    viewportWidth?: number;
    /** Hauteur du viewport (px). Défaut : viewportWidth ou maxDisplayPx. */
    viewportHeight?: number;
    /** Viewport carré (miniatures liste / garage). */
    maxDisplayPx?: number;
    /** Marge de cadrage — alignée sur GameScene FIT_PADDING (0.9). */
    fitPadding?: number;
  }
): void {
  if (circuit.path.length === 0) return;

  const viewportW = options?.viewportWidth ?? options?.maxDisplayPx ?? 480;
  const viewportH = options?.viewportHeight ?? options?.maxDisplayPx ?? viewportW;

  const gridStyle = options?.gridStyle ?? 'checker';
  const backgroundColor = options?.backgroundColor ?? readGameCanvasColor();
  const fitPadding = options?.fitPadding ?? TRACK_FIT_PADDING;

  canvas.width = viewportW;
  canvas.height = viewportH;

  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.fillStyle = backgroundColor;
  ctx.fillRect(0, 0, viewportW, viewportH);

  const zoom = calculateTrackZoomFit(
    circuit,
    GAME_TRACK_CELL_SIZE,
    viewportW,
    viewportH,
    fitPadding
  );
  const cellSize = GAME_TRACK_CELL_SIZE * zoom;
  const { offsetX, offsetY } = calculateTrackOffset(circuit, cellSize, viewportW, viewportH);

  const surface = new CanvasTrackDrawSurface(ctx);
  renderCircuit(surface, circuit, {
    offsetX,
    offsetY,
    cellSize,
    gridStyle,
  });
}
